import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { ONLY_TIME_FORMAT, TIME_FORMAT } from '@/app/constants/date'
import { AlertModel } from '@/app/models/alert/alertModel'
import { SingleAlertType } from '@/app/constants/singleAlertType'
import { DriverModel, VehicleModel } from '@/app/models'
import { useMutationAlert } from '../../hooks/useMutationAlert'

export type AlertConcerns = 'driver' | 'vehicle' | null

export type AlertFormContextType = {
  companyId: string
  editedAlert?: AlertModel | null
  drivers: DriverModel[]
  vehicles: VehicleModel[]

  type: SingleAlertType
  setType: (value: SingleAlertType) => void

  concerns: AlertConcerns
  setConcerns: (value: AlertConcerns) => void

  selectedDriverId: string
  setSelectedDriverId: (value: string) => void

  selectedVehicleId: string
  setSelectedVehicleId: (value: string) => void

  eventDate: Moment
  setEventDate: (value: Moment) => void

  eventTime: string
  setEventTime: (value: string) => void

  text: string
  setText: (value: string) => void

  reminderEnabled: boolean
  setReminderEnabled: (value: boolean) => void

  reminderDate: Moment | null
  setReminderDate: (value: Moment | null) => void

  reminderTime: string
  setReminderTime: (value: string) => void

  notRemindAnymore: boolean
  setNotRemindAnymore: (value: boolean) => void

  handleSubmit: (e?: React.FormEvent) => Promise<void>
  isPending: boolean
}

const AlertFormContext = React.createContext<AlertFormContextType | null>(null)

type ProviderProps = {
  children: React.ReactNode
  companyId: string
  editedAlert?: AlertModel | null
  drivers: DriverModel[]
  vehicles: VehicleModel[]
  handleClose: () => void
  onSave?: () => void
  onLoadingChange?: (loading: boolean) => void
}

export const AlertFormProvider = ({
  children,
  companyId,
  editedAlert,
  drivers,
  vehicles,
  handleClose,
  onSave,
  onLoadingChange,
}: ProviderProps) => {
  const mutation = useMutationAlert(companyId)

  const [type, setType] = React.useState<SingleAlertType>(
    editedAlert?.type || SingleAlertType.UserAlert,
  )
  const [concerns, setConcerns] = React.useState<AlertConcerns>(
    editedAlert?.driverId
      ? 'driver'
      : editedAlert?.vehicleId
        ? 'vehicle'
        : null,
  )
  const [selectedDriverId, setSelectedDriverId] = React.useState<string>(
    editedAlert?.driverId || '',
  )
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<string>(
    editedAlert?.vehicleId || '',
  )
  const [eventDate, setEventDate] = React.useState<Moment>(
    editedAlert?.eventTime || moment(),
  )
  const [eventTime, setEventTime] = React.useState<string>(
    editedAlert?.eventTime
      ? editedAlert.eventTime.format(ONLY_TIME_FORMAT)
      : moment().format(ONLY_TIME_FORMAT),
  )
  const [text, setText] = React.useState<string>(editedAlert?.text || '')
  const [reminderEnabled, setReminderEnabled] = React.useState<boolean>(
    !!editedAlert?.remindTime,
  )
  const [reminderDate, setReminderDate] = React.useState<Moment | null>(
    editedAlert?.remindTime || null,
  )
  const [reminderTime, setReminderTime] = React.useState<string>(
    editedAlert?.remindTime
      ? editedAlert.remindTime.format(TIME_FORMAT)
      : '00:00',
  )
  const [notRemindAnymore, setNotRemindAnymore] = React.useState<boolean>(
    editedAlert?.notRemindAnymore || false,
  )

  React.useEffect(() => {
    onLoadingChange?.(mutation.isPending)
  }, [mutation.isPending, onLoadingChange])

  const handleSubmit = React.useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      if (mutation.isPending) return

      const [eventHours, eventMinutes] = eventTime.split(':').map(Number)
      const finalEventTime = eventDate.clone().set({
        hour: eventHours,
        minute: eventMinutes,
        second: 0,
        millisecond: 0,
      })

      let finalRemindTime: Moment | null = null
      if (reminderEnabled && reminderDate) {
        const [reminderHours, reminderMinutes] = reminderTime
          .split(':')
          .map(Number)
        finalRemindTime = reminderDate.clone().set({
          hour: reminderHours,
          minute: reminderMinutes,
          second: 0,
          millisecond: 0,
        })
      }

      await mutation.mutateAsync({
        companyId,
        alertId: editedAlert?.alertId || '0',
        driverId: concerns === 'driver' ? selectedDriverId : '',
        vehicleId: concerns === 'vehicle' ? selectedVehicleId : '',
        eventTime: finalEventTime,
        remindTime: finalRemindTime,
        type,
        text,
        notRemindAnymore,
      })
      onSave?.()
      handleClose()
    },
    [
      mutation,
      eventTime,
      eventDate,
      reminderEnabled,
      reminderDate,
      reminderTime,
      companyId,
      editedAlert,
      concerns,
      selectedDriverId,
      selectedVehicleId,
      type,
      text,
      notRemindAnymore,
      onSave,
      handleClose,
    ],
  )

  const value = React.useMemo<AlertFormContextType>(
    () => ({
      companyId,
      editedAlert,
      drivers,
      vehicles,
      type,
      setType,
      concerns,
      setConcerns,
      selectedDriverId,
      setSelectedDriverId,
      selectedVehicleId,
      setSelectedVehicleId,
      eventDate,
      setEventDate,
      eventTime,
      setEventTime,
      text,
      setText,
      reminderEnabled,
      setReminderEnabled,
      reminderDate,
      setReminderDate,
      reminderTime,
      setReminderTime,
      notRemindAnymore,
      setNotRemindAnymore,
      handleSubmit,
      isPending: mutation.isPending,
    }),
    [
      companyId,
      editedAlert,
      drivers,
      vehicles,
      type,
      concerns,
      selectedDriverId,
      selectedVehicleId,
      eventDate,
      eventTime,
      text,
      reminderEnabled,
      reminderDate,
      reminderTime,
      notRemindAnymore,
      handleSubmit,
      mutation.isPending,
    ],
  )

  return (
    <AlertFormContext.Provider value={value}>
      {children}
    </AlertFormContext.Provider>
  )
}

export const useAlertForm= () => {
  const ctx = React.useContext(AlertFormContext)
  if (!ctx) {
    throw new Error(
      'useAlertFormS must be used within an AlertFormProvider',
    )
  }
  return ctx
}
